import {
  Dtos_V1_Equipment_EquipmentDtoFilterInput,
  Dtos_V1_Equipment_EquipmentDtoSortInput,
  Input_V2_Queries_Equipment_AvailableEquipmentsInput,
  useListAvailableEquipmentsQuery
} from '@/common/infrastructure/gql/generated/schema';
import { format } from 'date-fns';
import { isEqual } from 'lodash-es';
import { useMemo, useRef } from 'react';

import { mapEquipmentToTableData } from '@/equipments/application/mappers/EquipmentMappers';
import { EquipmentTableData } from '@/equipments/application/types/EquipmentTypes';

/**
 * Maps equipment items to table data format
 * @param items - Equipment items from the query
 * @returns Array of equipment table data
 */
const mapEquipmentsToTableData = (items: ReadonlyArray<unknown> | null | undefined): EquipmentTableData[] => {
  if (!items) return [];
  const mapped: EquipmentTableData[] = [];
  for (const item of items) {
    try {
      mapped.push(mapEquipmentToTableData(item as never));
    } catch {
      // Preserve list rendering by returning a safe fallback for the problematic item
      // and avoid bubbling errors into React render/memo phases
      const safe = typeof item === 'object' && item !== null ? (item as Record<string, unknown>) : {};
      const readNumber = (value: unknown, fallback = 0): number => (typeof value === 'number' ? value : fallback);
      const readString = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);
      const coerceNameObj = (value: unknown): { id: number; name: string } => {
        if (typeof value === 'object' && value !== null) {
          const rec = value as Record<string, unknown>;
          return { id: readNumber(rec.id), name: readString(rec.name) };
        }
        return { id: 0, name: '' };
      };
      const fallbackRow: EquipmentTableData = {
        id: readNumber(safe.id),
        name: readString(safe.name),
        description: '',
        deletable: true,
        make: { id: 0, name: '' },
        model: { id: 0, name: '' },
        year: 0,
        class: { id: 0, name: '' },
        type: coerceNameObj(safe.type),
        size: { id: 0, name: '' },
        status: coerceNameObj(safe.status),
        upcomingOutOfService: [],
        vinNumber: '',
        gpsTrackerSerialNumber: '',
        geoFencingEnabled: false,
        licensePlateNumber: '',
        licensePlateState: '',
        grossWeight: null,
        acquiredOn: null,
        beganServiceOn: null,
        purchasedMileage: null,
        beganServiceMileage: null,
        homeYard: '',
        currentYard: '',
        dispatchLocationName: '',
        mpEquipmentId: null,
        truckYear: null,
        truckModelNumber: '',
        machineSerialNumber: '',
        boomSerialNumber: '',
        defaultInsideSystemDiameter: null,
        defaultEndStyle: null,
        maxGroundBearingPressure: null,
        daysDown: null,
        boomModelNum: '',
        currentUnWorkable: null
      };
      mapped.push(fallbackRow);
    }
  }
  return mapped;
};

interface ListJobEquipmentsFilters {
  skip?: number;
  take?: number;
  where?: Dtos_V1_Equipment_EquipmentDtoFilterInput;
  order?: Dtos_V1_Equipment_EquipmentDtoSortInput[];
  /** When set, scopes availability to the selected customer (ERP job create/edit). */
  customerId?: number;
  /** When set, scopes availability to the selected job site (ERP job create/edit). */
  jobSiteId?: number;
}

/**
 * Hook to list available equipment for jobs
 * @param filters - Query filters for equipment
 * @returns Equipment data, loading state, error, and refetch function
 */
export const useListJobEquipments = (filters: ListJobEquipmentsFilters = { skip: 0, take: 10 }, startDate?: Date, endDate?: Date) => {
  const availableEquipmentsInput = useMemo((): Input_V2_Queries_Equipment_AvailableEquipmentsInput => {
    const input: Input_V2_Queries_Equipment_AvailableEquipmentsInput = {};
    if (startDate) {
      input.referenceStartDate = format(startDate, 'yyyy-MM-dd HH:mm:ss');
      if (endDate) {
        input.referenceEndDate = format(endDate, 'yyyy-MM-dd HH:mm:ss');
      }
    }
    if (filters.customerId != null && filters.customerId > 0) {
      input.customerId = filters.customerId;
    }
    if (filters.jobSiteId != null && filters.jobSiteId > 0) {
      input.jobSiteId = filters.jobSiteId;
    }
    return input;
  }, [startDate, endDate, filters.customerId, filters.jobSiteId]);

  // Store previous filters to detect changes
  const prevFiltersRef = useRef<ListJobEquipmentsFilters>(filters);

  // Only include defined filter fields and memoize based on actual filter values
  const memoizedFilters = useMemo(() => {
    const result: {
      skip?: number;
      take?: number;
      where?: Dtos_V1_Equipment_EquipmentDtoFilterInput;
      order?: Dtos_V1_Equipment_EquipmentDtoSortInput[];
    } = {};
    if (filters.skip !== undefined) result.skip = filters.skip;
    if (filters.take !== undefined) result.take = filters.take;
    if (filters.where !== undefined) result.where = filters.where;
    if (filters.order !== undefined) result.order = filters.order;

    // Update ref only if filters actually changed
    if (!isEqual(prevFiltersRef.current, filters)) {
      prevFiltersRef.current = filters;
    }

    return result;
  }, [filters]);

  const jobEquipmentFilters = useMemo(
    () => ({
      ...memoizedFilters,
      availableEquipmentsInput
    }),
    [memoizedFilters, availableEquipmentsInput]
  );

  const { data, loading, error, refetch } = useListAvailableEquipmentsQuery({
    variables: jobEquipmentFilters,
    fetchPolicy: 'cache-first',
    refetchWritePolicy: 'overwrite'
  });

  // Use the dedicated function to map equipment data
  const equipments: EquipmentTableData[] = useMemo(
    () => mapEquipmentsToTableData(data?.availableEquipments?.items),
    [data?.availableEquipments?.items]
  );

  const totalCount = data?.availableEquipments?.totalCount;

  return { equipments, totalCount, loading, error, refetch };
};
